import React from "react";
import { cn } from "../../lib/utils";
import { ShoppingListWithEditedFlags } from "../../utils/editUtils";
import { PurchaseDto } from "../../api";
import { getDepartmentIcon } from "../../data/department";
import {
  formatPurchaseDescription,
  getDefaultPurchaseUnit,
} from "../../utils/purchaseUtils";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/Accordion";
import { Checkbox } from "../ui/Checkbox";
import { NumberInput } from "../ui/Input";
import { Plan2PlateIcon } from "../Plan2PlateIcon";
import { Glyph, SmallIconButton } from "../SmallIconButton";

interface DepartmentGroupProps {
  value: string;
  department: string;
  departmentLabel: string;
  items: ShoppingListWithEditedFlags[];
  purchases: PurchaseDto[];
  onItemChange: (itemId: number, amountPurchased: number) => void;
}

export const DepartmentGroup = ({
  value,
  department,
  departmentLabel,
  items,
  purchases,
  onItemChange,
}: DepartmentGroupProps) => {
  const purchasedCount = items.filter(
    (item) => item.amountPurchased >= item.amount,
  ).length;
  const allDone = purchasedCount === items.length;

  let deptIcon: ReturnType<typeof getDepartmentIcon> | null = null;
  try {
    deptIcon = getDepartmentIcon(department);
  } catch {
    deptIcon = null;
  }

  return (
    <AccordionItem
      value={value}
      className="rounded-card border border-line bg-surface-card overflow-hidden"
    >
      <AccordionTrigger className="px-4 py-3">
        <div className="flex flex-1 items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            {deptIcon && (
              <span className={cn(allDone ? "opacity-40" : "", "flex")}>
                <Glyph icon={deptIcon} size={28} />
              </span>
            )}
            <span
              className={cn(
                "text-sm",
                allDone ? "text-ink-muted" : "font-bold text-ink",
              )}
            >
              {departmentLabel}
            </span>
          </div>
          <span
            className={cn(
              "text-xs mr-2",
              allDone ? "text-teal font-medium" : "text-ink-muted",
            )}
          >
            {purchasedCount}/{items.length}
          </span>
        </div>
      </AccordionTrigger>

      <AccordionContent className="border-t border-line">
        {items.map((item) => {
          const purchase = purchases.find(
            (p) => p.id === item.availabilityPurchaseId,
          );
          if (!purchase) return null;
          const defaultUnit = getDefaultPurchaseUnit(purchase);
          const fullyPurchased = item.amountPurchased >= item.amount;

          return (
            <div
              key={item.id}
              className="flex items-center gap-3 px-4 py-3 border-b border-line last:border-0 justify-between"
            >
              <div className="flex flex-col items-stretch w-full gap-2">
                <div className="flex flex-row gap-3 items-center">
                  <Checkbox
                    checked={fullyPurchased}
                    onCheckedChange={(checked) =>
                      onItemChange(item.id, checked ? item.amount : 0)
                    }
                  />
                  <span
                    className={cn(
                      "flex-1 text-sm text-ink",
                      fullyPurchased && "line-through text-ink-muted",
                    )}
                  >
                    {formatPurchaseDescription(purchase)}
                  </span>
                </div>
                <div className="flex flex-row items-center justify-between">
                  <span className="text-xs text-ink-muted whitespace-nowrap">
                    x {item.amount} {defaultUnit.description}
                  </span>
                  <div className="flex items-center gap-2">
                    <NumberInput
                      noWFull
                      className="h-8 w-24"
                      value={item.amountPurchased}
                      onValueChange={(v) => onItemChange(item.id, v ?? 0)}
                    />
                    <span className="text-sm text-ink-muted min-w-[3rem]">
                      {defaultUnit.description}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </AccordionContent>
    </AccordionItem>
  );
};
